import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { customAxios } from 'lib/axios';
import { User } from 'types';
import storage from 'utils/storage';

type UserResponse = {
  token: string;
  user: User;
};

type MfaRequiredResponse = {
  mfaRequired: true;
  mfaMethod: 'email' | 'totp';
  userId: string;
};

type LoginData = {
  email: string;
  password: string;
};

type RegisterData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

const login = async (
  loginData: LoginData
): Promise<UserResponse | MfaRequiredResponse> => {
  const { data } = await customAxios.post<UserResponse | MfaRequiredResponse>(
    '/users/login',
    loginData
  );
  return data;
};

export type AuthProviders = { google?: boolean; github?: boolean; microsoft?: boolean };

const getAuthProviders = async (): Promise<AuthProviders> => {
  const { data } = await customAxios.get<AuthProviders>('/auth/providers');
  return data;
};

const verifyMfaLogin = async ({
  userId,
  code,
}: {
  userId: string;
  code: string;
}): Promise<UserResponse> => {
  const { data } = await customAxios.post<UserResponse>('/users/login/mfa', {
    userId,
    code,
  });
  return data;
};

const register = async (registerData: RegisterData): Promise<UserResponse> => {
  const { data } = await customAxios.post<UserResponse>(
    '/users/register',
    registerData
  );
  return data;
};

export const useAuth = () => {
  const queryClient = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      if ('token' in data) {
        const { token, user } = data;
        storage.setToken(token);
        queryClient.setQueryData(['auth-user'], user);
      }
    },
  });

  const verifyMfaMutation = useMutation({
    mutationFn: verifyMfaLogin,
    onSuccess: (data) => {
      const { token, user } = data;
      storage.setToken(token);
      queryClient.setQueryData(['auth-user'], user);
    },
  });

  const registerMutation = useMutation({
    mutationFn: register,
    onSuccess: (data) => {
      if ('token' in data && data.token) {
        storage.setToken(data.token);
        queryClient.setQueryData(['auth-user'], data.user);
      }
    },
  });

  const logout = () => {
    storage.clearToken();
    queryClient.clear();
  };

  const providersQuery = useQuery({
    queryKey: ['auth-providers'],
    queryFn: getAuthProviders,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    login: loginMutation.mutate,
    isLoggingIn: loginMutation.isLoading,
    verifyMfaLogin: verifyMfaMutation.mutate,
    isVerifyingMfa: verifyMfaMutation.isLoading,
    register: registerMutation.mutate,
    isRegistering: registerMutation.isLoading,
    logout,
    providers: providersQuery.data ?? {},
    isLoadingProviders: providersQuery.isLoading,
  };
};
